import { useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../../../../../app/store';
import { useUpsertContractMutation } from '../../../../../../../services/ContractService';
import { initializeContract, clearSelectedContract } from '../../../../../../../services/ContractService/contractSlice';

interface UseAddContractFormResult {
    open: boolean;
    error: string | null;
    company: string;
    contract: ReturnType<typeof useAppSelector> extends { contract: { currentContract: infer T } } ? T : any;
    isLoading: boolean;
    isFormValid: boolean;
    handleOpen: () => void;
    handleClose: () => void;
    handleSave: () => Promise<void>;
}

export const useAddContractForm = (): UseAddContractFormResult => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const [open, setOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const company = searchParams.get('company') || '';
    const contract = useAppSelector((state) => state.contract.currentContract);

    const [upsertContract, { isLoading }] = useUpsertContractMutation();

    const handleOpen = useCallback(() => {
        dispatch(initializeContract({ company }));
        setOpen(true);
        setError(null);
    }, [dispatch, company]);

    const handleClose = useCallback(() => {
        setOpen(false);
        setError(null);
        dispatch(clearSelectedContract());
    }, [dispatch]);

    const handleSave = useCallback(async () => {
        if (!contract) {
            setError('No contract data available');
            return;
        }

        if (!contract.payer?.trim()) {
            setError('Payer is required');
            return;
        }

        if (!contract.product?.trim()) {
            setError('Product is required');
            return;
        }

        if (!contract.effectiveDate) {
            setError('Effective date is required');
            return;
        }

        try {
            const result = await upsertContract({
                contract: {
                    ...contract,
                    company,
                },
            }).unwrap();

            handleClose();
            navigate(`/contracts/edit?company=${encodeURIComponent(company)}&id=${encodeURIComponent(result.id)}`);
        } catch (err: any) {
            console.error('Failed to create contract:', err);
            setError(err?.data?.message || 'Failed to create contract. Please try again.');
        }
    }, [contract, company, upsertContract, handleClose, navigate]);

    const isFormValid = Boolean(
        contract?.payer?.trim() && 
        contract?.product?.trim() && 
        contract?.effectiveDate
    );

    return {
        open,
        error,
        company,
        contract,
        isLoading,
        isFormValid,
        handleOpen,
        handleClose,
        handleSave
    };
};
